import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Sprout, TreeDeciduous, Beef, Flower, Flower2, Droplet, Palmtree, Leaf } from 'lucide-react';
import '../styles/Home.css';

const Home = () => {
  useEffect(() => {
    // Fade-in animations on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fadeIn');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animated').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const verticals = [
    {
      icon: <Sprout className="vertical-icon" />,
      title: 'Organic Farming',
      description: 'Chemical-free cultivation practices that nurture soil health and produce naturally nutritious crops.',
      image: 'https://images.unsplash.com/photo-1529313780224-1a12b68bed16'
    },
    {
      icon: <TreeDeciduous className="vertical-icon" />,
      title: 'Land Development',
      description: 'Transforming barren lands into thriving agricultural ecosystems through sustainable practices.',
      image: 'https://images.unsplash.com/photo-1629379450846-fca660f551ea'
    },
    {
      icon: <Beef className="vertical-icon" />,
      title: 'Cattle Farming',
      description: 'Ethical livestock management integrated with organic farming for sustainable agriculture.',
      image: 'https://images.unsplash.com/photo-1492496913980-501348b61469'
    },
    {
      icon: <Flower className="vertical-icon" />,
      title: 'Mushroom Cultivation',
      description: 'Premium quality mushroom production using controlled environment agriculture techniques.',
      image: 'https://images.pexels.com/photos/2478421/pexels-photo-2478421.jpeg'
    },
    {
      icon: <Flower2 className="vertical-icon" />,
      title: 'Saffron (Kesar) Cultivation',
      description: 'Growing the world\'s most precious spice with traditional methods and modern precision.',
      image: 'https://images.unsplash.com/photo-1760982685687-fed74d29d73b'
    },
    {
      icon: <Droplet className="vertical-icon" />,
      title: 'Honey Extraction',
      description: 'Pure, natural honey harvested through ethical beekeeping practices that support pollination.',
      image: 'https://images.unsplash.com/photo-1749628523016-c33a7ecffa2a'
    },
    {
      icon: <Palmtree className="vertical-icon" />,
      title: 'Coconut Plantation',
      description: 'Sustainable coconut cultivation delivering multiple products from a single crop.',
      image: 'https://images.pexels.com/photos/6877970/pexels-photo-6877970.jpeg'
    },
    {
      icon: <Leaf className="vertical-icon" />,
      title: 'Arecanut Plantation',
      description: 'Traditional arecanut farming with modern agricultural techniques for optimal yield.',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449'
    }
  ];

  const whyChooseUs = [
    {
      title: 'Sustainable Practices',
      description: 'Every decision we make prioritizes environmental health and long-term sustainability.'
    },
    {
      title: 'Long-Term Land Value Creation',
      description: 'We build agricultural systems that increase land value while preserving natural resources.'
    },
    {
      title: 'Ethical Farming',
      description: 'Our practices respect animal welfare, soil health, and community well-being.'
    },
    {
      title: 'Integrated Farm Ecosystem Model',
      description: 'Multiple agricultural activities working in harmony for maximum efficiency and sustainability.'
    },
    {
      title: 'Experienced Management',
      description: 'Decades of combined expertise in agriculture, land development, and sustainable practices.'
    }
  ];

  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '' // Spam protection
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage({
          type: 'success',
          text: data.message
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          honeypot: ''
        });
      } else {
        setSubmitMessage({
          type: 'error',
          text: data.detail || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessage({
        type: 'error',
        text: 'Failed to send message. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="home-container">
      {/* Navigation Header */}
      <header className="network-header">
        <div className="nav-wrapper">
          <a href="/" className="network-logo">Navira Naturals</a>
          <nav className="network-nav">
            <a href="#about" className="network-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>About</a>
            <a href="#verticals" className="network-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('verticals'); }}>Our Verticals</a>
            <a href="#why-us" className="network-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('why-us'); }}>Why Us</a>
            <a href="#partner" className="network-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('partner'); }}>Partner</a>
            <a href="#contact" className="network-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('https://images.unsplash.com/photo-1767452433319-12b4d7e6ec91?q=80&w=2000')`
      }}>
        <div className="hero-content">
          <h1 className="hero-title">Navira Naturals</h1>
          <p className="hero-tagline">CRAFTING PURITY FROM THE EARTH</p>
          <p className="hero-subtext">
            Building sustainable agri-ecosystems through responsible land development and natural cultivation practices.
          </p>
          <div className="hero-buttons">
            <Button 
              className="btn-secondary hero-btn"
              onClick={() => scrollToSection('verticals')}
            >
              Explore Our Verticals
            </Button>
            <Button 
              className="btn-primary hero-btn"
              onClick={() => scrollToSection('partner')}
            >
              Partner With Us
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section animated">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">About Navira Naturals</h2>
              <p className="section-description">
                Navira Naturals is dedicated to creating sustainable agricultural ecosystems that combine traditional wisdom with modern practices. We focus on land development, organic cultivation, livestock integration, and plantation management to deliver long-term value while respecting nature.
              </p>
              <p className="section-description">
                Our approach goes beyond conventional farming. We create integrated agricultural systems where each element supports the others—from soil health to biodiversity, from water conservation to carbon sequestration. This holistic model ensures productivity, profitability, and planetary health work in harmony.
              </p>
            </div>
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1761228694529-a49c3eb47dfa?q=80&w=800" 
                alt="Sustainable farmland" 
                className="rounded-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Verticals Section */}
      <section id="verticals" className="verticals-section animated">
        <div className="container">
          <h2 className="section-title centered">Our Verticals</h2>
          <p className="section-subtitle centered">
            Diverse agricultural practices united by our commitment to sustainability and excellence
          </p>
          <div className="verticals-grid">
            {verticals.map((vertical, index) => (
              <Card key={index} className="vertical-card">
                <div className="vertical-image-container">
                  <img 
                    src={`${vertical.image}?q=80&w=600`} 
                    alt={vertical.title}
                    className="vertical-image"
                  />
                  <div className="vertical-overlay">
                    {vertical.icon}
                  </div>
                </div>
                <div className="vertical-content">
                  <h3 className="vertical-title">{vertical.title}</h3>
                  <p className="vertical-description">{vertical.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="why-section animated">
        <div className="container">
          <h2 className="section-title centered">Why Navira Naturals</h2>
          <p className="section-subtitle centered">
            Our commitment to excellence and sustainability sets us apart
          </p>
          <div className="why-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="why-card">
                <div className="why-checkmark">✓</div>
                <h3 className="why-title">{item.title}</h3>
                <p className="why-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section id="partner" className="partner-section animated" style={{
        backgroundImage: `linear-gradient(rgba(63, 89, 61, 0.85), rgba(63, 89, 61, 0.85)), url('https://images.pexels.com/photos/6964971/pexels-photo-6964971.jpeg?q=80&w=2000')`
      }}>
        <div className="container">
          <div className="partner-content">
            <h2 className="section-title white-text">Let's Build Sustainable Value Together</h2>
            <p className="section-description white-text">
              We welcome investors, landowners, and strategic partners to collaborate in building scalable and sustainable agricultural ventures. Together, we can create agricultural ecosystems that deliver financial returns while nurturing the earth.
            </p>
            <Button 
              className="btn-primary large-btn"
              onClick={() => scrollToSection('contact')}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section animated">
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2 className="section-title">Get In Touch</h2>
              <p className="section-description">
                Have questions about our verticals or interested in partnership opportunities? We'd love to hear from you.
              </p>
              <div className="contact-details">
                <p className="contact-detail-item">
                  <strong>Building Sustainable Agriculture</strong><br />
                  One Partnership at a Time
                </p>
              </div>
            </div>
            <div className="contact-form-container">
              <form className="contact-form" onSubmit={handleContactSubmit}>
                {/* Honeypot field - hidden from users, visible to bots */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleInputChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                
                {submitMessage.text && (
                  <div className={`submit-message ${submitMessage.type}`}>
                    {submitMessage.text}
                  </div>
                )}
                
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                    className="form-input"
                    placeholder="Your full name"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                    className="form-input"
                    placeholder="your@email.com"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required 
                    className="form-input"
                    placeholder="+91 XXXXX XXXXX"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea 
                    id="message" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="5" 
                    required 
                    className="form-input"
                    placeholder="Tell us about your interest or inquiry..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>
                <Button 
                  type="submit" 
                  className="btn-primary full-width"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-title">Navira Naturals</h3>
              <p className="footer-tagline">Sustainable Agriculture & Natural Cultivation</p>
              <p className="footer-unit">A Unit of Vaaraahi Ventures</p>
            </div>
            <div className="footer-links">
              <h4 className="footer-heading">Quick Links</h4>
              <a href="#about" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>About</a>
              <a href="#verticals" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToSection('verticals'); }}>Our Verticals</a>
              <a href="#partner" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToSection('partner'); }}>Partner With Us</a>
              <a href="#contact" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a>
            </div>
            <div className="footer-social">
              <h4 className="footer-heading">Connect With Us</h4>
              <div className="social-icons">
                <a href="#" className="social-icon" aria-label="Facebook">f</a>
                <a href="#" className="social-icon" aria-label="Instagram">ig</a>
                <a href="#" className="social-icon" aria-label="LinkedIn">in</a>
                <a href="#" className="social-icon" aria-label="Twitter">tw</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="copyright">&copy; 2026 Navira Naturals. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;